import { type CSSProperties, useCallback, useEffect, useRef } from 'react';

import { Text } from '@revelio/ui';

import { GLOBAL_AI_INLINE_CHART_LOADING_HEIGHT } from '../CustomDash/constants/panelWidths';
import styles from './global-ai-assistant-chart-preview-slot.module.css';
import { useGlobalAIAssistant } from './global-ai-assistant-context';

export function GlobalAIAssistantChartPreviewSlot({
  messageId,
  intersectionRoot,
  variant = 'chart',
}: {
  messageId: string;
  /** Transcript scroll container; when null, visibility uses the viewport. */
  intersectionRoot: HTMLElement | null;
  variant?: 'chart' | 'text';
}) {
  const {
    registerChartPreviewMount,
    setChartPreviewSlotViewportVisible,
    liveChartPreviewMessageIds,
    chartPreviewRuntimeByMessageId,
  } = useGlobalAIAssistant();

  const isLive = liveChartPreviewMessageIds.includes(messageId);
  const runtimePhase = chartPreviewRuntimeByMessageId[messageId]?.phase;
  const isTextVariant = variant === 'text';
  const slotMinHeight =
    isTextVariant || runtimePhase === 'success'
      ? 'min-content'
      : GLOBAL_AI_INLINE_CHART_LOADING_HEIGHT;
  const elementRef = useRef<HTMLDivElement | null>(null);

  const setMountRef = useCallback(
    (node: HTMLDivElement | null) => {
      elementRef.current = node;
      registerChartPreviewMount(messageId, node);
    },
    [messageId, registerChartPreviewMount]
  );

  useEffect(() => {
    const el = elementRef.current;
    if (!el) {
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        setChartPreviewSlotViewportVisible(messageId, entry.isIntersecting);
      },
      {
        root: intersectionRoot,
        threshold: 0,
        rootMargin: '0px 0px 160px 0px',
      }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
    };
  }, [messageId, intersectionRoot, setChartPreviewSlotViewportVisible]);

  return (
    <div className={styles.slot}>
      {!isLive && !isTextVariant ? (
        <Text fontSize="x-sm" className={styles.mutedLabel} tone="muted">
          Chart preview idle — scroll this message into view to load.
        </Text>
      ) : null}
      <div
        ref={setMountRef}
        className={styles.mount}
        style={{ '--slot-min-height': slotMinHeight } as CSSProperties}
        data-testid={`global-ai-assistant-chart-slot-${messageId}`}
      />
    </div>
  );
}
